import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { getAllBranch, getCustomerGroupExportSettings } from "../../services";
import { pushGlobalError } from "../../errors/globalError";
import {
  ALL_OPTION,
  CUSTOMER_GROUP_EXPORTERS,
  downloadBlob,
  getCustomerGroupExportFilename,
  getDefaultExportDate,
  getExportDateParam,
  getExportErrorMessage,
  getFilenameFromContentDisposition,
  isExportRangeTooLong,
  MAX_EXPORT_RANGE_DAYS,
  toExportChains,
} from "./utils";
import { ExportChain } from "../../types/sales-report-export.type";
import { Branch } from "../../types/user.type";
import { getProfile, KeycloakProfile } from "../../services/keycloak";
import { useUserRole } from "../../utils/hooks/useUserRole";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import CustomerGroupSelector from "./CustomerGroupSelector";
import SalesReportFilter from "./SalesReportFilter";

dayjs.locale("th");

const RANGE_TOO_LONG_TEXT = `เลือกช่วงวันที่ได้ไม่เกิน ${MAX_EXPORT_RANGE_DAYS} วัน`;

export default function SalesReportExport() {
  const { isHeadOfficeAccounting } = useUserRole();
  const defaultDate = useMemo(() => getDefaultExportDate(), []);

  const [chains, setChains] = useState<ExportChain[]>([]);
  const [chainsLoaded, setChainsLoaded] = useState<boolean>(false);
  const [selectedChain, setSelectedChain] = useState<ExportChain | null>(null);
  const [dateFrom, setDateFrom] = useState<Dayjs>(defaultDate);
  const [dateTo, setDateTo] = useState<Dayjs>(defaultDate);
  const [selectedRoute, setSelectedRoute] = useState<string>(ALL_OPTION);
  const [selectedBranch, setSelectedBranch] = useState<string>(ALL_OPTION);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [exporting, setExporting] = useState<boolean>(false);
  const [userProfile, setUserProfile] = useState<KeycloakProfile>();

  useEffect(() => {
    const onGetProfile = async () => {
      const profile = await getProfile();
      setUserProfile(profile);
    };

    onGetProfile();
  }, []);

  useEffect(() => {
    const onGetChains = async () => {
      try {
        const {
          data: { data },
        } = await getCustomerGroupExportSettings();
        setChains(toExportChains(data ?? []));
        setChainsLoaded(true);
      } catch {
        // Surfaced by the axios interceptor's error toast. The cards stay hidden rather
        // than claiming that no chain is set up.
      }
    };

    onGetChains();
  }, []);

  const onGetAllBranch = useCallback(async () => {
    if (!isHeadOfficeAccounting) return;
    const {
      data: { data },
    } = await getAllBranch();
    setBranches(data);
  }, [isHeadOfficeAccounting]);

  useEffect(() => {
    onGetAllBranch();
  }, [onGetAllBranch]);

  // HO ส่ง branch_code ของสาขาที่เลือก (map จาก company_id) — เลือก "ทั้งหมด"
  // จะได้ undefined จึงไม่ส่ง branch_code ไป BE = ทุกสาขา / Branch ใช้สาขาตาม JWT
  const exportBranchCode = useMemo(() => {
    if (isHeadOfficeAccounting) {
      return branches.find((branch) => branch.company_id === selectedBranch)
        ?.branch_code;
    }
    return userProfile?.attributes.branchCode.toString();
  }, [isHeadOfficeAccounting, branches, selectedBranch, userProfile]);

  const handleChangeDateRange = (from: Dayjs, to: Dayjs) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const isRangeTooLong = isExportRangeTooLong(dateFrom, dateTo);
  const isDownloadDisabled = exporting || isRangeTooLong;

  const handleDownload = async () => {
    if (!selectedChain) return;

    const exporter = CUSTOMER_GROUP_EXPORTERS[selectedChain.export_format];
    setExporting(true);
    try {
      const response = await exporter({
        customer_group_uuid: selectedChain.uuid,
        date_from: getExportDateParam(dateFrom),
        date_to: getExportDateParam(dateTo),
        branch_code: exportBranchCode,
        route: selectedRoute,
      });
      const filename =
        getFilenameFromContentDisposition(
          response.headers["content-disposition"],
        ) || getCustomerGroupExportFilename(selectedChain, dateFrom, dateTo);

      downloadBlob(filename, response.data);
    } catch (error) {
      const message = await getExportErrorMessage(error);
      if (message) pushGlobalError(message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Typography variant="h6" mb={2}>
        รายงานการขายเฉพาะกลุ่ม
      </Typography>

      <Card sx={{ boxShadow: "none", borderRadius: "12px" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack gap={3}>
            {chainsLoaded && (
              <CustomerGroupSelector
                chains={chains}
                selectedUuid={selectedChain?.uuid ?? null}
                onSelect={setSelectedChain}
              />
            )}

            {selectedChain && (
              <Stack>
                <SalesReportFilter
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  onChangeDateRange={handleChangeDateRange}
                  selectedRoute={selectedRoute}
                  setSelectedRoute={setSelectedRoute}
                  selectedBranch={selectedBranch}
                  setSelectedBranch={setSelectedBranch}
                  branches={branches}
                />
                {isRangeTooLong && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {RANGE_TOO_LONG_TEXT}
                  </Alert>
                )}
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    disabled={isDownloadDisabled}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Download
                  </Button>
                </Box>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
